import React, { useCallback, useMemo, useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useInvestorsSelector } from "store";
import { ISaveDealAsTemplateData } from "store/types/templates";

import { PickerFilterBar } from "./PickerFilterBar";
import { PickerHeader } from "./PickerHeader";
import { SelectionPanel } from "./SelectionPanel";
import { border, s } from "../styles";
import { TemplateTreeView } from "./TemplateTreeView";
import { PickerModeConfig } from "../hooks/use-picker-filters";
import { useTemplateSelection } from "../hooks/use-template-selection";

const TEMPLATE_CONFIG: PickerModeConfig = {
  title: "Save as template",
  subtitle: "Choose the folders and files new deals should inherit",
  showTemplateName: true,
  foldersSelectable: true,
  showViewToggle: false,
  showUploadButton: false,
  showMagicButton: false,
  confirmLabel: () => "Save as template"
};

export type TemplateSubmitResult = { templateName: string; sections: ISaveDealAsTemplateData["sections"] };

/* ─── Template mode props ─── */
export interface TemplatePickerProps {
  mode: "template";
  onSubmit: (result: TemplateSubmitResult) => void;
  onCancel: () => void;
  /**
   * Performs the save while the form stays open. If it rejects (e.g. duplicate
   * title) the error is shown inline and the form remains open. When omitted,
   * the result is just handed back to the caller via onSubmit.
   */
  onConfirm?: (result: TemplateSubmitResult) => Promise<void>;
}

export const TemplatePicker: React.FC<TemplatePickerProps> = ({ onSubmit, onCancel, onConfirm }) => {
  const config = TEMPLATE_CONFIG;
  const selection = useTemplateSelection();
  const investors = useInvestorsSelector();
  const investorMap = useMemo(() => {
    const map = new Map<number, string>();
    investors.forEach((inv) => map.set(inv.id, inv.name));
    return map;
  }, [investors]);

  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const isDisabled = !selection.state.folders.length || !selection.templateName.trim();

  const handleSubmit = useCallback(async () => {
    if (isDisabled || saving) return;
    const result: TemplateSubmitResult = {
      templateName: selection.templateName,
      sections: selection.buildPayload()
    };

    if (!onConfirm) {
      onSubmit(result);
      return;
    }

    setSaving(true);
    setNameError(null);
    try {
      await onConfirm(result);
      onSubmit(result);
    } catch (err) {
      const apiError = err as { errors?: Record<string, string[]>; message?: string };
      const fieldMessage = apiError?.errors?.title?.[0];
      setNameError(fieldMessage || apiError?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [isDisabled, saving, selection, onConfirm, onSubmit]);

  const handleNameChange = useCallback(
    (value: string) => {
      selection.setTemplateName(value);
      if (nameError) setNameError(null);
    },
    [selection, nameError]
  );

  return (
    <Box
      sx={{
        width: "90vw",
        maxWidth: 1100,
        maxHeight: "calc(100vh - 48px)",
        bgcolor: "background.paper",
        borderRadius: "12px",
        border: border(),
        boxShadow: "0px 10px 10px -5px rgba(18, 18, 23, 0.04), 0px 20px 25px -5px rgba(18, 18, 23, 0.10)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        outline: "none"
      }}
    >
      {/* Header */}
      <Box sx={{ flexShrink: 0 }}>
        <PickerHeader title={config.title} subtitle={config.subtitle} onClose={onCancel} />
      </Box>

      {/* Template name */}
      <Box sx={{ px: "20px", pt: "16px", pb: "10px", flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          id="picker-template-name"
          label="Template Name"
          placeholder="Name this template"
          value={selection.templateName}
          onChange={(e) => handleNameChange(e.target.value)}
          error={!!nameError}
          helperText={nameError || undefined}
          sx={{ "& .MuiInputBase-root": { height: 32 } }}
        />
      </Box>

      {/* Controls - Filters + Search */}
      <Box sx={{ flexShrink: 0 }}>
        <PickerFilterBar
          filters={selection.filters}
          hasActiveFilters={selection.hasActiveFilters}
          classificationOptions={selection.classificationOptions}
          setClassifications={selection.setClassifications}
          setInvestors={selection.setInvestors}
          setEffectiveDate={selection.setEffectiveDate}
          clearFilter={selection.clearFilter}
          clearAllFilters={selection.clearAllFilters}
        >
          <TextField
            size="small"
            placeholder="Search file name"
            value={selection.searchQuery}
            onChange={(e) => selection.setSearchQuery(e.target.value)}
            sx={{ width: 232, flexShrink: 0, "& .MuiInputBase-root": { height: 32 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                </InputAdornment>
              )
            }}
          />
        </PickerFilterBar>
      </Box>

      {/* Results summary */}
      {(selection.hasActiveFilters || selection.searchQuery) && (
        <Box sx={{ px: "20px", py: "8px", borderBottom: border(), flexShrink: 0 }}>
          <Typography sx={{ fontSize: 15 }}>
            Filter result: <strong>{selection.filteredFileCount} files</strong>
          </Typography>
        </Box>
      )}

      {/* Content area (the only scrolling region) */}
      <Box sx={s.contentArea}>
        <TemplateTreeView selection={selection} investorMap={investorMap} />
      </Box>

      {/* Selection panel */}
      <SelectionPanel mode="template" selection={selection} />

      {/* Footer */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        gap="10px"
        sx={{ px: "20px", py: "14px", borderTop: border(), flexShrink: 0 }}
      >
        <Button variant="outlined" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={isDisabled || saving} onClick={handleSubmit}>
          {saving ? "Saving…" : config.confirmLabel(selection.selectedCount)}
        </Button>
      </Stack>
    </Box>
  );
};

/* ─── Prompt-compatible template picker ─── */
export interface TemplatePickerPromptProps {
  onSubmit: (result: TemplateSubmitResult) => void;
  onCancel: () => void;
  onConfirm?: (result: TemplateSubmitResult) => Promise<void>;
}

export const TemplateContentPicker: React.FC<TemplatePickerPromptProps> = ({ onSubmit, onCancel, onConfirm }) => {
  return <TemplatePicker mode="template" onSubmit={onSubmit} onCancel={onCancel} onConfirm={onConfirm} />;
};
